import { IcBadge, IcCorner } from '@assets/components/cockpitSidebar';
import { Box, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import useClickOutside from '@hooks/useClickOutside';
import { FunctionComponent, SVGProps } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { CockpitPanelIntlKeysEnum } from '../cockpit.types';
import { useCockpitUiStateContext } from '../CockpitContexts';
import { PANEL_ROUTES } from '../CockpitRoutes';

type Options =
  | {
      label: string;
      subMenuItems: {
        icon: FunctionComponent<SVGProps<SVGSVGElement>>;
        label: CockpitPanelIntlKeysEnum;
        isHidden?: boolean;
      }[];
      onClick?: never;
    }
  | {
      label: string;
      subMenuItems?: never;
      onClick: () => void;
    }
  | {
      label: CockpitPanelIntlKeysEnum;
      subMenuItems?: never;
      onClick?: never;
    };

type Props = {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  isForcedActive?: boolean;
  isHidden?: boolean;
  badge?: boolean;
  count?: number;
} & Options;

export default function CockpitSidebarButton({
  icon,
  isForcedActive = false,
  isHidden,
  label,
  badge = false,
  count,
  subMenuItems,
  onClick,
}: Readonly<Props>) {
  const { ref, isOpen, toggle, close } = useClickOutside();
  const { isSidebarMaximized, setIsSidebarMaximized, openedPanel } = useCockpitUiStateContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const hasSubItems = subMenuItems !== undefined;
  const isActive =
    isForcedActive || openedPanel === label || subMenuItems?.some((item) => item.label === openedPanel) || isOpen;
  const color = isActive ? 'neutral.200' : 'neutral.500';

  if (isHidden || (hasSubItems && subMenuItems.every((item) => item.isHidden))) {
    return null;
  }

  const handleMenuClick = () => {
    if (hasSubItems) {
      toggle();
    } else {
      onClick ? onClick() : navigate(PANEL_ROUTES[label]);
      setIsSidebarMaximized(false);
    }
  };

  const handleSubMenuClick = (subMenuLabel: CockpitPanelIntlKeysEnum) => {
    navigate(PANEL_ROUTES[subMenuLabel]);
    close();
    setIsSidebarMaximized(false);
  };

  return (
    <Box position="relative" width="100%" height="64px" userSelect="none" ref={ref}>
      <Tooltip
        content={formatMessage({ id: `components.sidebar.${label}` })}
        disabled={isSidebarMaximized}
        positioning={{
          placement: 'right',
        }}
        openDelay={800}
      >
        <HStack
          backgroundColor={isOpen ? 'blue.600' : 'neutral.black'}
          width="100%"
          height="100%"
          position="relative"
          cursor="pointer"
          gap={4}
          className="group"
          borderLeft="4px solid"
          borderColor={isOpen ? 'skyBlue' : 'neutral.black'}
          justifyContent="start"
          flexShrink={0}
          _hover={{
            backgroundColor: 'blue.600',
            borderColor: 'skyBlue',
          }}
          onClick={handleMenuClick}
        >
          <VStack gap="5px" width="24px" position="relative" marginLeft={2.5}>
            <Icon as={icon} boxSize="24px" color={color} _groupHover={{ color: 'skyBlue' }} />
            {badge && (
              <Icon
                as={IcBadge}
                position="absolute"
                top="-6px"
                right="-6px"
                boxSize="11px"
                color={isOpen ? 'blue.600' : 'neutral.black'}
                _groupHover={{ color: 'blue.600' }}
              />
            )}
            {count !== undefined && (
              <Text fontWeight="medium" textStyle="xs">
                {count}
              </Text>
            )}
          </VStack>
          {hasSubItems && (
            <Icon
              as={IcCorner}
              boxSize="8px"
              position="absolute"
              right={isSidebarMaximized ? '7px' : '17px'}
              bottom={isSidebarMaximized ? '5px' : '10px'}
              color={color}
              _groupHover={{ color: 'skyBlue' }}
            />
          )}
          {isSidebarMaximized && (
            <Text
              textStyle="sm"
              color={isActive ? 'neutral.white' : 'neutral.300'}
              lineClamp={2}
              width="100%"
              whiteSpace="pre-line"
              _groupHover={{ color: 'neutral.white' }}
            >
              {formatMessage({ id: `components.sidebar.${label}` })}
            </Text>
          )}
        </HStack>
      </Tooltip>
      {hasSubItems && isOpen && (
        <VStack
          gap={0.25}
          width="max-content"
          flexShrink={0}
          padding={0.25}
          backgroundColor="neutral.black"
          position="absolute"
          right={0}
          top="-2px"
          transform="translateX(100%)"
        >
          <For each={subMenuItems.filter((subMenuItem) => !subMenuItem.isHidden)}>
            {(subMenuItem) => {
              const isSubActive = openedPanel === subMenuItem.label;
              const subIconColor = isSubActive ? 'neutral.200' : 'neutral.500';
              return (
                <HStack
                  height="64px"
                  width="100%"
                  borderLeft="4px solid"
                  borderColor="neutral.black"
                  paddingX={2.5}
                  gap={4}
                  key={subMenuItem.label}
                  backgroundColor="neutral.black"
                  className="group"
                  _hover={
                    isSubActive
                      ? undefined
                      : {
                          backgroundColor: 'blue.600',
                          borderColor: 'skyBlue',
                        }
                  }
                  cursor={isSubActive ? 'default' : 'pointer'}
                  onClick={isSubActive ? undefined : () => handleSubMenuClick(subMenuItem.label)}
                >
                  <Icon
                    as={subMenuItem.icon}
                    boxSize="24px"
                    color={subIconColor}
                    _groupHover={isSubActive ? undefined : { color: 'skyBlue', borderColor: 'skyBlue' }}
                  />
                  <Text
                    width="100%"
                    color={isSubActive ? 'neutral.white' : 'neutral.300'}
                    _groupHover={isSubActive ? undefined : { color: 'neutral.white' }}
                  >
                    {formatMessage({ id: `components.sidebar.${subMenuItem.label}` })}
                  </Text>
                </HStack>
              );
            }}
          </For>
        </VStack>
      )}
    </Box>
  );
}
